import { Tournament } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Button } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import { ActivityIndicator, Image, ScrollView, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from './CreatePostScreen.styles';

export interface CreatePostScreenProps {
  content: string;
  onContentChange: (text: string) => void;
  imageUrl?: string;
  onImagePick: () => void;
  onImageRemove: () => void;
  uploading: boolean;
  tournaments: Tournament[];
  selectedTournamentId?: string;
  onTournamentSelect: (tournamentId?: string) => void;
  onCreatePost: () => void;
  onCancel: () => void;
  creating: boolean;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const CreatePostScreen = ({
  content,
  onContentChange,
  imageUrl,
  onImagePick,
  onImageRemove,
  uploading,
  tournaments,
  selectedTournamentId,
  onTournamentSelect,
  onCreatePost,
  onCancel,
  creating,
  colors,
}: CreatePostScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();
  const charCount = content.length;
  const maxChars = 500;
  const isOverLimit = charCount > maxChars;
  const canCreate = content.trim().length > 0 && !isOverLimit && !uploading && !creating;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={onCancel} disabled={creating}>
          <Text style={[styles.cancelText, { color: colors.primary }]}>{t('common.cancel')}</Text>
        </TouchableOpacity>
        <Text style={[styles.title, { color: colors.text }]}>{t('feed.createPost')}</Text>
        <TouchableOpacity onPress={onCreatePost} disabled={!canCreate}>
          <Text
            style={[
              styles.createText,
              { color: canCreate ? colors.primary : colors.textTertiary },
            ]}
          >
            {creating ? t('feed.posting') : t('feed.post')}
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.scrollContent}>
        {/* Text Input */}
        <View style={styles.section}>
          <TextInput
            style={[
              styles.textInput,
              {
                backgroundColor: colors.card,
                borderColor: isOverLimit ? colors.error : colors.border,
                color: colors.text,
              },
            ]}
            placeholder={t('feed.whatsOnYourMind')}
            placeholderTextColor={colors.textTertiary}
            value={content}
            onChangeText={onContentChange}
            multiline
            textAlignVertical="top"
            maxLength={maxChars + 50} // Allow typing a bit over to show error
            editable={!creating}
          />
          <View style={styles.charCountContainer}>
            <Text
              style={[
                styles.charCount,
                { color: isOverLimit ? colors.error : colors.textSecondary },
              ]}
            >
              {charCount} / {maxChars}
            </Text>
          </View>
        </View>

        {/* Image Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('createPost.photoOptional')}</Text>
          {imageUrl ? (
            <View style={styles.imageContainer}>
              <Image source={{ uri: imageUrl }} style={styles.imagePreview} resizeMode="cover" />
              <TouchableOpacity
                style={[styles.removeImageButton, { backgroundColor: colors.error }]}
                onPress={onImageRemove}
                disabled={creating}
              >
                <Ionicons name="close" size={20} color="white" />
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              style={[styles.uploadButton, { backgroundColor: colors.card, borderColor: colors.border }]}
              onPress={onImagePick}
              disabled={uploading || creating}
            >
              {uploading ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <>
                  <Ionicons name="camera-outline" size={32} color={colors.textSecondary} />
                  <Text style={[styles.uploadText, { color: colors.textSecondary }]}>{t('createPost.addPhoto')}</Text>
                </>
              )}
            </TouchableOpacity>
          )}
        </View>

        {/* Tournament Picker */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('createPost.tournamentOptional')}</Text>
          <View style={[styles.pickerContainer, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Picker
              selectedValue={selectedTournamentId}
              onValueChange={(value) => onTournamentSelect(value)}
              enabled={!creating}
              style={{ color: colors.text }}
            >
              <Picker.Item label={t('createPost.none')} value={undefined} />
              {tournaments.map((tournament) => (
                <Picker.Item key={tournament.id} label={tournament.name} value={tournament.id} />
              ))}
            </Picker>
          </View>
        </View>

        {/* Create Button */}
        <View style={styles.buttonContainer}>
          <Button
            title={creating ? t('createPost.creatingPost') : t('feed.createPost')}
            onPress={onCreatePost}
            disabled={!canCreate}
            variant="primary"
            colors={colors}
          />
        </View>
      </ScrollView>
    </View>
  );
};
